<template>
  <el-dialog
    v-model="dialogVisible"
    title="新用户激活 / 忘记密码"
    width="440px"
    :close-on-click-modal="false"
    :before-close="handleClose"
    append-to-body
  >
    <div v-if="!isSuccess">
      <div class="dialog-notice">
        提示：输入您的工号和验证码，系统将向绑定的邮箱发送激活与密码设置链接（链接30分钟内有效）。
      </div>
      <el-form
        ref="formRef"
        :model="form"
        :rules="rules"
        label-width="70px"
        @submit.prevent="handleSubmit"
      >
        <el-form-item label="工号" prop="username">
          <el-input
            v-model="form.username"
            placeholder="请输入工号或用户名"
            clearable
            autocomplete="off"
            tabindex="1"
          />
        </el-form-item>
        <el-form-item label="验证码" prop="verifyCode">
          <div class="code-wrapper">
            <el-input
              v-model="form.verifyCode"
              placeholder="请输入验证码"
              clearable
              autocomplete="off"
              tabindex="2"
              @keyup.enter="handleSubmit"
            />
            <img
              v-if="captchaImg"
              :src="captchaImg"
              alt="点击刷新验证码"
              title="点击刷新验证码"
              class="captcha-img"
              @click="refreshCaptcha"
            />
          </div>
        </el-form-item>
      </el-form>
    </div>

    <div v-else class="success-box">
      <div class="success-icon-wrap">
        <CircleCheck style="width: 48px; height: 48px; color: #67c23a;" />
      </div>
      <div class="success-title">邮件发送成功</div>
      <div class="success-desc">
        系统已向绑定邮箱 <span class="highlight-email">{{ maskedEmail }}</span> 发送了激活与密码设置链接。
      </div>
      <div class="success-subdesc">
        请在 30 分钟内查收邮件并完成新密码设置。
      </div>
    </div>

    <template #footer>
      <span class="dialog-footer" v-if="!isSuccess">
        <el-button @click="handleClose" type="danger">取消</el-button>
        <el-button
          type="primary"
          :loading="loading"
          @click="handleSubmit"
        >
          确定
        </el-button>
      </span>
      <span class="dialog-footer" v-else>
        <el-button type="primary" @click="handleClose">我知道了</el-button>
      </span>
    </template>
  </el-dialog>
</template>

<script lang="ts">
import { defineComponent, reactive, ref, toRefs, onMounted, watch } from 'vue'
import { ElMessage } from 'element-plus'
import { CircleCheck } from '@element-plus/icons-vue'
import { getVerifiCode, sendActivationEmail } from '@/apis/user'

export default defineComponent({
  name: 'ActivationDialog',
  components: {
    CircleCheck
  },
  props: {
    visible: {
      type: Boolean,
      default: false
    }
  },
  emits: ['update:visible', 'close'],
  setup(props, { emit }) {
    const formRef = ref<any>(null)
    const state = reactive({
      dialogVisible: props.visible,
      loading: false,
      isSuccess: false,
      maskedEmail: '',
      captchaImg: '',
      form: {
        username: '',
        verifyCode: '',
        id: ''
      },
      rules: {
        username: [{ required: true, message: '请输入工号或用户名', trigger: ['blur', 'change'] }],
        verifyCode: [{ required: true, message: '请输入验证码', trigger: ['blur', 'change'] }]
      }
    })

    watch(() => props.visible, (val) => {
      state.dialogVisible = val
      if (val) {
        state.isSuccess = false
        state.form.username = ''
        state.form.verifyCode = ''
        refreshCaptcha()
      }
    })

    const refreshCaptcha = () => {
      getVerifiCode().then((res: any) => {
        if (res) {
          state.captchaImg = res.image
          state.form.id = res.id
        }
      }).catch((err) => {
        console.error('获取图形验证码失败', err)
      })
    }

    const handleClose = () => {
      emit('update:visible', false)
      emit('close')
    }

    const handleSubmit = () => {
      if (!formRef.value) return
      formRef.value.validate((valid: boolean) => {
        if (!valid) return
        state.loading = true
        sendActivationEmail({
          username: state.form.username.trim(),
          verifyCode: state.form.verifyCode.trim(),
          verifyCodeId: state.form.id
        }).then((res: any) => {
          state.loading = false
          state.maskedEmail = (res && res.email) || '您的邮箱'
          state.isSuccess = true
          ElMessage.success('激活/重置邮件发送成功，请查收邮箱')
        }).catch(() => {
          state.loading = false
          state.form.verifyCode = ''
          refreshCaptcha()
        })
      })
    }

    onMounted(() => {
      if (props.visible) {
        refreshCaptcha()
      }
    })

    return {
      formRef,
      ...toRefs(state),
      refreshCaptcha,
      handleClose,
      handleSubmit
    }
  }
})
</script>

<style lang="scss" scoped>
.dialog-notice {
  margin-bottom: 20px;
  font-size: 14px;
  color: #606266;
  line-height: 1.6;
}

.code-wrapper {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;

  .el-input {
    flex: 1;
  }

  .captcha-img {
    width: 110px;
    height: 38px;
    border-radius: 4px;
    cursor: pointer;
    border: 1px solid #dcdfe6;
    object-fit: cover;
    background: #fff;
    user-select: none;
    transition: opacity 0.2s;

    &:hover {
      opacity: 0.85;
    }
  }
}

.success-box {
  text-align: center;
  padding: 10px 0 10px;

  .success-icon-wrap {
    display: flex;
    justify-content: center;
    margin-bottom: 12px;
  }

  .success-title {
    font-size: 16px;
    font-weight: 600;
    color: #303133;
    margin-bottom: 8px;
  }

  .success-desc {
    font-size: 14px;
    color: #606266;
    line-height: 1.6;
    margin-bottom: 6px;

    .highlight-email {
      color: #409EFF;
      font-weight: bold;
    }
  }

  .success-subdesc {
    font-size: 12px;
    color: #909399;
  }
}

.dialog-footer {
  text-align: center;
}
</style>
